import { weekIdToTimestamp, getNextYearWeekId } from 'src/utils/dateUtils';
import _cloneDeep from 'lodash/cloneDeep';
import { safeDivide } from 'src/utils/app';

export type TimeSeriesPoint = [number, number]; // [weekId, value]

type ComputeCommand = 'average' | 'sum';

type ComputeOption = {
  /**
   * Specify the range of week numbers to be used in the computation.
   * If an array is provided, the data points whose week numbers fall within the range will be used.
   * If the start and end of the range are the same, only the data points with that specific week number will be used.
   * @example range: [202301, 202352] => data points between week numbers 202301 and 202352 inclusive
   * @example range: [202403, 202403] => data points with week number 202403
   */
  range?: [number, number];
};

/**
 * Class to handle time series data for spline charts
 */
export class TimeSeriesData {
  private data: TimeSeriesPoint[];

  constructor(data: TimeSeriesPoint[]) {
    this.data = _cloneDeep(data);
  }

  public createWeekRecords<T extends { weekId: number }>(
    records: T[],
    valueExtractor: (record: T) => number
  ): TimeSeriesData {
    const points: TimeSeriesPoint[] = records
      .sort((a, b) => a.weekId - b.weekId)
      .map((record) => [record.weekId, valueExtractor(record)]);

    this.data = points;
    return this;
  }

  /**
   * Converts week IDs to Unix timestamps
   */
  private toUnix() {
    const transformedData: TimeSeriesPoint[] = this.data.map(([weekId, value]) => {
      return [weekIdToTimestamp(weekId), value];
    });

    return transformedData;
  }

  /**
   * Sorts the data by weekId in ascending order
   * @param compareFn - function to compare two TimeSeriesPoint
   */
  private sort(compareFn = (a: TimeSeriesPoint, b: TimeSeriesPoint) => a[0] - b[0]): TimeSeriesData {
    const sortedData = [...this.data].sort(compareFn);
    this.data = sortedData;
    return this;
  }

  /**
   * Converts all week IDs in the series to the next year.
   * If the weekId is 202101, it will be converted to 202201
   */
  public convertTimeToNextYear() {
    this.data = this.data.map(([weekId, value]) => [getNextYearWeekId(weekId), value]);
    return this;
  }

  /**
   * Transforms the data to be consumed by the spline chart by sorting and converting week IDs to Unix timestamps
   * @deprecated Use toWeekIdSeries() for SplineChartV2 which uses category-based x-axis
   */
  public toChartSeries() {
    return this.clone().sort().toUnix();
  }

  /**
   * Returns sorted data with raw weekIds (no timestamp conversion).
   * Use this for SplineChartV2 which uses category-based x-axis with weekIds.
   */
  public toWeekIdSeries(): TimeSeriesPoint[] {
    return this.clone().sort().getPoints();
  }

  /**
   * Adds a point to start of the series
   */
  public unshift(point: TimeSeriesPoint): TimeSeriesData {
    this.data = [point, ...this.data];
    return this;
  }

  public getLastElement(): TimeSeriesPoint {
    if (this.data.length === 0) {
      return null;
    }
    return [...this.data[this.data.length - 1]];
  }

  public isValid(): boolean {
    return this.data.length > 0;
  }

  public clone(): TimeSeriesData {
    return new TimeSeriesData([...this.data]);
  }

  public getPoints(): TimeSeriesPoint[] {
    return [...this.data];
  }

  private getComputationData(commandOption?: ComputeOption): TimeSeriesPoint[] {
    if (!commandOption?.range) {
      return this.getPoints();
    }

    const { range } = commandOption;

    if (Array.isArray(range)) {
      const [start, end] = range;
      const points = this.getPoints();

      // If start equals end, we want just that specific week
      if (start === end) {
        // Find the point with the matching week number
        return points.filter(([weekId]) => {
          return weekId === start;
        });
      }

      // For normal ranges, filter points whose week numbers fall within the range
      return points.filter(([weekId]) => {
        return weekId >= start && weekId <= end;
      });
    }

    return this.getPoints();
  }

  compute(command: ComputeCommand, commandOption?: ComputeOption): number {
    if (this.isValid()) {
      const data = this.getComputationData(commandOption);

      switch (command) {
        case 'average':
          return safeDivide(
            data.reduce((acc, [_, value]) => acc + value, 0),
            data.length
          );
        case 'sum':
          return data.reduce((acc, [_, value]) => acc + value, 0);

        default:
          throw new Error('Invalid computation command');
      }
    }
    return 0;
  }
}
